import { lightFormat } from 'date-fns';
import { TblCol, TblColType } from '../table.types';
import utilSvc from '../../utilSvc';

export function filterTypeDROPTEXT(dataVal: any, flt: any): boolean {
    const fltDropTxt = typeof flt === 'string' ? flt.toLowerCase() : '';
    let dataValStr = '';
    if (typeof dataVal === 'string') {
        dataValStr = dataVal;
    } else if (Array.isArray(dataVal)) {
        dataValStr = dataVal.join(',');
    }
    return dataValStr.toLowerCase() == fltDropTxt;
}

export function filterTypeDATE(dataVal: any, flt: any): boolean {
    try {
        const dataValTxt = lightFormat(dataVal, 'M/d/yyyy');
        const dataValTxt2 = lightFormat(dataVal, utilSvc.dt.fmtMdyyyyhmmssa);
        const fltDateTxt = typeof flt === 'string' ? flt.toLowerCase() : '';
        return (
            (typeof dataValTxt === 'string' && dataValTxt.includes(fltDateTxt)) ||
            (typeof dataValTxt2 === 'string' && dataValTxt2.includes(fltDateTxt))
        );
    } catch (e) {
        return false;
    }
}

export function filterTypeMULTITEXT(dataVal: any, flt: any): boolean {
    const fltMultiTxt = typeof flt === 'string' ? flt.toLowerCase() : '';
    const regex = /,/g;
    const fltClean = fltMultiTxt.replace(regex, ' ').split(' ');
    if (!Array.isArray(dataVal)) {
        return false;
    }
    return fltClean.every(fltItem => {
        return dataVal.some(item => {
            let itemStr = '';
            if (typeof item === 'string') {
                itemStr = item;
            } else if (Array.isArray(item)) {
                itemStr = item.join(',');
            }
            return itemStr.toLowerCase().includes(fltItem);
        });
    });
}

export function filterTypeTEXT(dataVal: any, flt: any): boolean {
    const fltTxt = typeof flt === 'string' ? flt.toLowerCase() : '';
    let dataValStrText = '';
    if (typeof dataVal === 'string') {
        dataValStrText = dataVal;
    } else if (Array.isArray(dataVal)) {
        dataValStrText = dataVal.join(',');
    }
    return dataValStrText.toLowerCase().includes(fltTxt);
}

export function filterTypeNUMBER(dataVal: any, flt: any): boolean {
    const fltNum = typeof flt === 'string' ? flt : '';
    const dataValStrNum = typeof dataVal === 'number' ? dataVal.toString() : typeof dataVal === 'string' ? dataVal : '';
    return dataValStrNum.includes(fltNum);
}

export function filterTypeCOMMA_SEPARATED_NUMBER(dataVal: any, flt: any): boolean {
    const fltNumComma = typeof flt === 'string' ? flt.replace(/,/g, '') : '';
    const dataValStrNumComma =
        typeof dataVal === 'number' ? dataVal.toString() : typeof dataVal === 'string' ? dataVal.replace(/,/g, '') : '';
    return dataValStrNumComma.includes(fltNumComma);
}

// Matches against the displayed 2-decimal amount (e.g. 36000.00, 7991.04) so
// users can filter by the decimal portion they see. Strips $, commas and spaces
// from the filter term.
export function filterTypeCURRENCY(dataVal: any, flt: any): boolean {
    const fltNum = typeof flt === 'string' ? flt.replace(/[$,\s]/g, '') : '';
    const num = typeof dataVal === 'number' ? dataVal : parseFloat(String(dataVal ?? '').replace(/[^0-9.-]/g, ''));
    if (Number.isNaN(num)) {
        return false;
    }
    return num.toFixed(2).includes(fltNum);
}

export function filterTypeMULTISELECT(dataVal: any, flt: any): boolean {
    if (!Array.isArray(flt)) {
        return true; // No filter applied, show all
    }
    if (flt.length === 0) {
        return false; // All options deselected, show no rows
    }
    const dataValString = typeof dataVal === 'string' ? dataVal : '';
    return flt.includes(dataValString);
}

export function filterTypeBOOLEAN(dataVal: any, flt: any): boolean {
    const mappedFltValue = normalizeBooleanFilter(flt);
    let dataValModified;
    if (typeof dataVal === 'boolean') {
        dataValModified = dataVal ? 'YES' : 'NO';
    } else {
        dataValModified = '';
    }
    return mappedFltValue !== undefined && dataValModified === mappedFltValue;
}

function normalizeBooleanFilter(val: string): 'YES' | 'NO' | undefined {
    if (!val) return undefined;
    const v = val.trim().toUpperCase();
    if (v === 'Y' || v === 'YE' || v === 'YES') return 'YES';
    if (v === 'N' || v === 'NO') return 'NO';
    return undefined;
}

export function tableDataFilterItems<T>(items: T[], cols: TblCol[], filters: object): T[] {
    // null array (this happens from golang)
    if (!Array.isArray(items) || items.length < 1) {
        return [];
    }

    // columns or filters isn't ready, avoid blow up
    if (!Array.isArray(cols) || cols.length < 1 || Object.keys(filters).length === 0) {
        return items;
    }

    let result: T[] = Array.from(items);
    let flt: string;
    let colType: TblColType = '';

    for (const prop in filters) {
        if (!filters.hasOwnProperty(prop)) {
            continue;
        }

        // valid flt with key and value
        flt = filters[prop];
        if (!flt) {
            continue;
        }

        // find col type
        const foundColumn = cols.find(col => col.key === prop);
        if (!foundColumn || !foundColumn.type) {
            continue;
        }
        colType = foundColumn.type;

        // apply filter by type
        result = result.filter(item => {
            const dataVal = item[prop];
            switch (colType) {
                case 'DROPTEXT':
                    return filterTypeDROPTEXT(dataVal, flt);
                case 'DATE':
                    return filterTypeDATE(dataVal, flt);
                case 'MULTITEXT':
                    return filterTypeMULTITEXT(dataVal, flt);
                case 'TEXT':
                    return filterTypeTEXT(dataVal, flt);
                case 'NUMBER':
                    return filterTypeNUMBER(dataVal, flt);
                case 'COMMA_SEPARATED_NUMBER':
                    return filterTypeCOMMA_SEPARATED_NUMBER(dataVal, flt);
                case 'CURRENCY':
                    return filterTypeCURRENCY(dataVal, flt);
                case 'MULTISELECT':
                    return filterTypeMULTISELECT(dataVal, flt);
                case 'BOOLEAN':
                    return filterTypeBOOLEAN(dataVal, flt);
                default:
                    throw new Error('tableDataFilterItems: col type not supported: ' + colType);
            }
        });

        if (result.length < 1) {
            break;
        }
    }

    return result;
}
